/* eslint-disable react/prop-types */
import { EmoNeutral } from "@assets"
import { Head, Link, usePage } from "@inertiajs/react"
import {
  BookOpen,
  Calendar,
  Heart,
  List,
  MessageSquare,
  ShoppingBag,
  Store,
  Ticket,
} from "lucide-react"

import FeedItem, {
  type FeedItem as FeedItemData,
} from "@/components/feed/feed-item"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import {
  eventsPath,
  feedPath,
  forumPath,
  interviewsPath,
  marketplacePath,
  rafflesPath,
} from "@/routes"
import type { SharedData } from "@/types"

interface Product {
  id: number
  type?: string
  source_id?: number
  name: string
  description?: string
  price: number
  sale_price?: number
  current_price?: number
  stock_quantity: number
  condition: string
  created_at: string
  favorited_at: string
  business: {
    id: number
    name: string
    verified: boolean
  }
  category: {
    id: number
    name: string
    slug?: string
  }
  neighborhood?: {
    id: number
    name: string
    slug?: string
  }
  reviews?: {
    id: number
    rating: number
    comment?: string
  }[]
  images?: {
    id: number
    url: string
  }[]
  is_favorited?: boolean
  favorites_count?: number
}

interface InterviewItem {
  id: number | string
  type?: string
  source_id?: number
  title: string
  interviewee_name?: string
  excerpt?: string
  created_at?: string
  favorited_at: string
  cover_url?: string | null
  author?: { id: number; name: string; verified?: boolean }
  neighborhood?: { id: number; name: string }
  is_favorited?: boolean
  favorites_count?: number
}

interface Business {
  id: number
  type?: string
  source_id?: number
  name?: string
  business_name: string
  title?: string
  business_type: string
  category?: string
  description: string
  address: string
  phone: string
  website?: string
  rating?: number
  total_reviews?: number
  is_verified: boolean
  verified?: boolean
  created_at: string
  favorited_at: string
  neighborhood?: {
    id: number
    name: string
    slug?: string
  }
  owner?: {
    id: number
    name: string
    avatar_url?: string | null
    verified?: boolean
  }
  reviews: { id: number; rating: number }[]
  images?: string[]
  is_favorited?: boolean
  favorites_count?: number
}

interface Raffle {
  id: number
  type: "raffle"
  source_id: number
  created_at: string
  title: string
  description?: string
  prize?: string
  start_date?: string
  end_date: string
  prize_value?: number
  winner_count?: number
  max_participants?: number
  sponsor?: {
    id: number
    name: string
    verified?: boolean
  }
  neighborhood?: {
    id: number
    name: string
  }
  image_url?: string | null
  status?: string
  is_active?: boolean
  is_upcoming?: boolean
  has_ended?: boolean
  participants_count?: number
  participants?: number
  is_entered?: boolean
  raffle_entries?: { id: number }[]
  favorited_at: string
  is_favorited?: boolean
  favorites_count?: number
}

interface EventItem {
  id: number
  title: string
  description?: string | { body?: string }
  category: string
  created_at?: string
  start_date: string
  end_date?: string
  venue_name?: string
  address?: string
  price?: number
  is_free: boolean
  capacity?: number
  featured: boolean
  organizer: { id: number; name: string; verified?: boolean }
  neighborhood?: { id: number; name: string }
  event_attendees: { id: number; status: string }[]
  attendees_today?: number
  participants?: number
  is_attending?: boolean
  favorited_at: string
  is_favorited?: boolean
  favorites_count?: number
}

interface ForumPostItem {
  id: number
  title: string
  content: string | { body?: string }
  description?: string
  slug: string
  view_count: number
  is_resolved: boolean
  is_pinned: boolean
  created_at: string
  favorited_at: string
  category: { id: number; name: string; icon?: string; color?: string }
  author: { id: number; name: string; verified?: boolean }
  neighborhood?: { id: number; name: string }
  forum_answers: { id: number }[]
  forum_votes: { vote_type: string }[]
  tags?: string[]
  is_favorited?: boolean
  favorites_count?: number
}

interface PageProps {
  products: Product[]
  businesses: Business[]
  raffles: Raffle[]
  events: EventItem[]
  posts: ForumPostItem[]
  interviews: InterviewItem[]
}

export default function FavoritesIndex() {
  const { props } = usePage<SharedData & PageProps>()
  const {
    products = [],
    businesses = [],
    raffles = [],
    events = [],
    posts = [],
    interviews = [],
  } = props

  // Combinar todos los favoritos en un solo array
  const allFavorites = [
    ...products.map((p) => ({
      type: "product" as const,
      id: p.id,
      source_id: p.source_id ?? p.id,
      created_at: p.created_at,
      favorited_at: p.favorited_at,
      name: p.name,
      description: p.description,
      images: p.images?.map((img) => img.url),
      price: p.price,
      sale_price: p.sale_price,
      current_price: p.current_price,
      condition: p.condition,
      business: {
        id: p.business.id,
        name: p.business.name,
        verified: p.business.verified,
      },
      is_favorited: p.is_favorited,
      favorites_count: p.favorites_count,
    })),
    ...businesses.map((b) => ({
      type: "business" as const,
      id: b.id,
      source_id: b.source_id ?? b.id,
      created_at: b.created_at,
      favorited_at: b.favorited_at,
      name: b.name ?? b.business_name,
      description: b.description,
      category: b.category ?? b.business_type,
      address: b.address,
      rating: b.rating,
      total_reviews: b.total_reviews,
      neighborhood: b.neighborhood,
      owner: b.owner
        ? { ...b.owner, avatar_url: b.owner.avatar_url ?? undefined }
        : undefined,
      images: b.images,
      verified: b.verified ?? b.is_verified,
      is_favorited: b.is_favorited,
      favorites_count: b.favorites_count,
    })),
    ...raffles.map((r) => ({
      type: "raffle" as const,
      id: r.id,
      source_id: r.source_id ?? r.id,
      created_at: r.created_at,
      favorited_at: r.favorited_at,
      title: r.title,
      description: r.description,
      end_date: r.end_date,
      prize_value: r.prize_value,
      winner_count: r.winner_count ?? 1,
      raffle_entries: r.raffle_entries ?? [],
      participants_count: r.participants_count,
      sponsor: r.sponsor,
      image_url: r.image_url,
      is_entered: r.is_entered,
      is_favorited: r.is_favorited,
      favorites_count: r.favorites_count,
    })),
    ...events.map((e) => ({
      type: "event" as const,
      id: e.id,
      source_id: e.id,
      created_at: e.start_date,
      favorited_at: e.favorited_at,
      title: e.title,
      description: e.description,
      start_date: e.start_date,
      end_date: e.end_date,
      venue_name: e.venue_name,
      address: e.address,
      price: e.price,
      is_free: Boolean(e.is_free),
      featured: e.featured,
      category: e.category,
      organizer: {
        id: e.organizer.id,
        name: e.organizer.name,
        verified: e.organizer.verified,
      },
      neighborhood: e.neighborhood,
      event_attendees: e.event_attendees ?? [],
      attendees_today: e.attendees_today,
      is_attending: e.is_attending,
      is_favorited: e.is_favorited,
      favorites_count: e.favorites_count,
    })),
    ...posts.map((p) => ({
      type: "forum_post" as const,
      id: p.id,
      source_id: p.id,
      created_at: p.created_at,
      favorited_at: p.favorited_at,
      title: p.title,
      content: p.content,
      category: p.category,
      forum_answers: p.forum_answers ?? [],
      forum_votes: p.forum_votes ?? [],
      view_count: p.view_count,
      is_pinned: p.is_pinned,
      author: {
        id: p.author.id,
        name: p.author.name,
        verified: p.author.verified ?? false,
      },
      is_favorited: p.is_favorited,
      favorites_count: p.favorites_count,
    })),
    ...interviews.map((i) => ({
      type: "interview" as const,
      id: i.id,
      source_id:
        i.source_id ?? (typeof i.id === "number" ? i.id : Number(i.id)),
      created_at: i.created_at ?? new Date().toISOString(),
      favorited_at: i.favorited_at,
      title: i.title,
      interviewee_name: i.interviewee_name,
      excerpt: i.excerpt ?? "",
      cover_url: i.cover_url ?? undefined,
      author: i.author
        ? { id: i.author.id, name: i.author.name, verified: i.author.verified }
        : undefined,
      is_favorited: i.is_favorited,
      favorites_count: i.favorites_count,
    })),
  ].sort(
    (a, b) =>
      new Date(b.favorited_at).getTime() - new Date(a.favorited_at).getTime(),
  ) // Ordenar por fecha de favorito

  return (
    <AppLayout title="Mis favoritos">
      <Head title="Mis favoritos" />

      <div className="mx-auto max-w-6xl space-y-6 md:p-6">
        <div className="flex flex-col items-center justify-center gap-4">
          <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
            <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
              <Heart className="text-primary h-8 w-8" />
            </span>
            Mis favoritos
          </h1>
          <p className="text-muted-foreground mt-1 text-center text-balance">
            Accede rápidamente a tus productos, negocios, sorteos, eventos y
            entrevistas guardados.
          </p>
        </div>

        {/* Tabs Navigation */}
        <Tabs defaultValue="all" className="space-y-6">
          <div className="flex justify-center">
            <TabsList className="mx-1 flex h-auto flex-wrap gap-2">
              <TabsTrigger value="all">
                <List className="h-4 w-4 shrink-0" />
                Todos
              </TabsTrigger>
              <TabsTrigger value="products">
                <ShoppingBag className="h-4 w-4 shrink-0" />
                Productos
              </TabsTrigger>
              <TabsTrigger value="businesses">
                <Store className="h-4 w-4 shrink-0" />
                Negocios
              </TabsTrigger>
              <TabsTrigger value="raffles">
                <Ticket className="h-4 w-4 shrink-0" />
                Sorteos
              </TabsTrigger>
              <TabsTrigger value="events">
                <Calendar className="h-4 w-4 shrink-0" />
                Eventos
              </TabsTrigger>
              <TabsTrigger value="posts">
                <MessageSquare className="h-4 w-4 shrink-0" />
                Posts
              </TabsTrigger>
              <TabsTrigger value="interviews">
                <BookOpen className="h-4 w-4 shrink-0" />
                Entrevistas
              </TabsTrigger>
            </TabsList>
          </div>

          {/* All Tab */}
          <TabsContent value="all">
            {allFavorites.length === 0 ? (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>

                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No tienes favoritos guardados
                </h3>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Link href={feedPath()}>
                    <Button size="sm">Ver Feed</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div>
                {allFavorites.map((item) => (
                  <FeedItem
                    variant="card"
                    key={`${item.type}-${item.id}`}
                    item={item as FeedItemData}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            {products.length === 0 ? (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>

                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No tienes productos en favoritos
                </h3>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Link href={marketplacePath()}>
                    <Button size="sm">Ver Market</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {products.map((p) => (
                  <FeedItem
                    key={p.id}
                    variant="grid"
                    item={
                      {
                        type: "product",
                        id: p.id,
                        source_id: p.source_id ?? p.id,
                        created_at: p.created_at,
                        name: p.name,
                        description: p.description,
                        images: p.images?.map((img) => img.url),
                        price: p.price,
                        sale_price: p.sale_price,
                        current_price: p.current_price,
                        condition: p.condition,
                        business: {
                          id: p.business.id,
                          name: p.business.name,
                          verified: p.business.verified,
                        },
                        is_favorited: p.is_favorited,
                        favorites_count: p.favorites_count,
                      } as FeedItemData
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Interviews Tab */}
          <TabsContent value="interviews">
            {interviews.length === 0 ? (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>

                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No tienes entrevistas en favoritos
                </h3>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Link href={interviewsPath()}>
                    <Button size="sm">Ver Entrevistas</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div>
                {interviews.map((i) => (
                  <FeedItem
                    key={i.id}
                    variant="card"
                    item={
                      {
                        type: "interview",
                        id: i.id,
                        source_id:
                          i.source_id ??
                          (typeof i.id === "number" ? i.id : Number(i.id)),
                        created_at: i.created_at ?? new Date().toISOString(),
                        title: i.title,
                        interviewee_name: i.interviewee_name,
                        excerpt: i.excerpt ?? "",
                        cover_url: i.cover_url ?? undefined,
                        author: i.author
                          ? {
                              id: i.author.id,
                              name: i.author.name,
                              verified: i.author.verified,
                            }
                          : undefined,
                        is_favorited: i.is_favorited,
                        favorites_count: i.favorites_count,
                      } as FeedItemData
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Businesses Tab */}
          <TabsContent value="businesses">
            {businesses.length === 0 ? (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>

                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No tienes negocios en favoritos
                </h3>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Link href={marketplacePath()}>
                    <Button size="sm">Ver Market</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div>
                {businesses.map((b) => (
                  <FeedItem
                    key={b.id}
                    variant="card"
                    item={
                      {
                        type: "business",
                        id: b.id,
                        source_id: b.source_id ?? b.id,
                        created_at: b.created_at,
                        name: b.name ?? b.business_name,
                        description: b.description,
                        category: b.category ?? b.business_type,
                        address: b.address,
                        rating: b.rating,
                        total_reviews: b.total_reviews,
                        neighborhood: b.neighborhood,
                        owner: b.owner
                          ? {
                              ...b.owner,
                              avatar_url: b.owner.avatar_url ?? undefined,
                            }
                          : undefined,
                        images: b.images,
                        verified: b.verified ?? b.is_verified,
                        is_favorited: b.is_favorited,
                        favorites_count: b.favorites_count,
                      } as FeedItemData
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Raffles Tab */}
          <TabsContent value="raffles">
            {raffles.length === 0 ? (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>

                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No tienes sorteos en favoritos
                </h3>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Link href={rafflesPath()}>
                    <Button size="sm">Ver Sorteos</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div>
                {raffles.map((r) => (
                  <FeedItem
                    variant="card"
                    key={r.id}
                    item={
                      {
                        type: "raffle",
                        id: r.id,
                        source_id: r.source_id ?? r.id,
                        created_at: r.created_at,
                        title: r.title,
                        description: r.description,
                        end_date: r.end_date,
                        prize_value: r.prize_value,
                        winner_count: r.winner_count ?? 1,
                        raffle_entries: r.raffle_entries ?? [],
                        participants_count: r.participants_count,
                        sponsor: r.sponsor,
                        image_url: r.image_url,
                        is_entered: r.is_entered,
                        is_favorited: r.is_favorited,
                        favorites_count: r.favorites_count,
                      } as FeedItemData
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            {events.length === 0 ? (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>

                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No tienes eventos en favoritos
                </h3>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Link href={eventsPath()}>
                    <Button size="sm">Ver Eventos</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div>
                {events.map((e) => (
                  <FeedItem
                    key={e.id}
                    variant="card"
                    item={
                      {
                        type: "event",
                        id: e.id,
                        source_id: e.id,
                        created_at: e.start_date,
                        title: e.title,
                        description: e.description,
                        start_date: e.start_date,
                        end_date: e.end_date,
                        venue_name: e.venue_name,
                        address: e.address,
                        price: e.price,
                        is_free: Boolean(e.is_free),
                        featured: e.featured,
                        category: e.category,
                        organizer: {
                          id: e.organizer.id,
                          name: e.organizer.name,
                          verified: e.organizer.verified,
                        },
                        neighborhood: e.neighborhood,
                        event_attendees: e.event_attendees ?? [],
                        attendees_today: e.attendees_today,
                        is_attending: e.is_attending,
                        is_favorited: e.is_favorited,
                        favorites_count: e.favorites_count,
                      } as FeedItemData
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Forum Posts Tab */}
          <TabsContent value="posts">
            {posts.length === 0 ? (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>

                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No tienes post en favoritos
                </h3>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Link href={forumPath()}>
                    <Button size="sm">Ver Foro</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div>
                {posts.map((p) => (
                  <FeedItem
                    key={p.id}
                    variant="card"
                    item={
                      {
                        type: "forum_post",
                        id: p.id,
                        source_id: p.id,
                        created_at: p.created_at,
                        title: p.title,
                        content:
                          typeof p.content === "string"
                            ? p.content
                            : (p.content?.body ?? ""),
                        author: {
                          id: p.author.id,
                          name: p.author.name,
                          verified: p.author.verified ?? false,
                        },
                        category: { id: p.category.id, name: p.category.name },
                        forum_answers: p.forum_answers ?? [],
                        forum_votes: p.forum_votes ?? [],
                        view_count: p.view_count,
                        is_pinned: p.is_pinned,
                        is_favorited: p.is_favorited,
                        favorites_count: p.favorites_count,
                      } as FeedItemData
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  )
}
